import axios from "axios";
import apiUrlOne from "../../utils.js/Config";

/* Fetch all bookings with optional filters */
export const getAllBookings = async (filters = {}) => {
    try {
        const params = new URLSearchParams();

        // Add filters to params if they exist
        if (filters.search) {
            params.append('search', filters.search);
        }
        if (filters.bookingStatus) {
            params.append('bookingStatus', filters.bookingStatus);
        }
        if (filters.paymentStatus) {
            params.append('paymentStatus', filters.paymentStatus);
        }
        if (filters.bookingType) {
            params.append('bookingType', filters.bookingType);
        }
        if (filters.startDate) {
            params.append('startDate', filters.startDate);
        }
        if (filters.endDate) {
            params.append('endDate', filters.endDate);
        }
        if (filters.page) {
            params.append('page', filters.page);
        }
        if (filters.pageSize) {
            params.append('pageSize', filters.pageSize);
        }
        // Send booking ID if provided (for individual booking)
        if (filters.id) {
            params.append('id', Number(filters.id));
        }

        const url = params.toString()
            ? `${apiUrlOne}/bookings/admin/all?${params.toString()}`
            : `${apiUrlOne}/bookings/admin/all`;

        const token = localStorage.getItem('accessToken');
        const headers = {
            'accept': '*/*',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.get(url, { headers });
        return response.data;
    } catch (error) {
        console.error("Error fetching bookings:", error);
        throw error;
    }
};

/* Fetch a single booking by ID */
export const getBookingById = async (bookingId) => {
    try {
        const numericBookingId = Number(bookingId);
        if (!Number.isInteger(numericBookingId) || numericBookingId <= 0) {
            throw new Error("Invalid booking ID: must be a positive integer");
        }

        const token = localStorage.getItem('accessToken');
        const headers = {
            'accept': '*/*',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.get(`${apiUrlOne}/bookings/${numericBookingId}`, { headers });
        return response.data.data;
    } catch (error) {
        console.error("Error fetching booking:", error);
        throw error;
    }
};

/* Fetch payment transactions with optional filters */
export const getPaymentTransactions = async (filters = {}) => {
    try {
        const params = new URLSearchParams();

        // Add filters to params if they exist
        if (filters.bookingId) {
            params.append('bookingId', Number(filters.bookingId));
        }
        if (filters.status) {
            params.append('status', filters.status);
        }
        if (filters.gateway) {
            params.append('gateway', filters.gateway);
        }
        if (filters.page) {
            params.append('page', filters.page);
        }
        if (filters.pageSize) {
            params.append('pageSize', filters.pageSize);
        }

        const url = params.toString()
            ? `${apiUrlOne}/payment-transactions?${params.toString()}`
            : `${apiUrlOne}/payment-transactions`;

        const token = localStorage.getItem('accessToken');
        const headers = {
            'accept': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.get(url, { headers });
        return response.data;
    } catch (error) {
        console.error("Error fetching payment transactions:", error);
        throw error;
    }
};

/* Fetch bookings for a specific retreat */
export const getBookingsByRetreatId = async (retreatId, page = 1, limit = 10) => {
    try {
        const numericRetreatId = Number(retreatId);
        if (!Number.isInteger(numericRetreatId) || numericRetreatId <= 0) {
            throw new Error("Invalid retreat ID: must be a positive integer");
        }

        const token = localStorage.getItem('accessToken');
        const headers = {
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.get(`${apiUrlOne}/bookings/retreat/${numericRetreatId}?page=${page}&limit=${limit}`, { headers });
        return response.data;
    } catch (error) {
        console.error(`Error fetching bookings for retreat ${retreatId}:`, error);
        throw error;
    }
};

/* Fetch single payment transaction by ID */
export const getPaymentTransactionById = async (id) => {
    try {
        const numericId = Number(id);
        if (!Number.isInteger(numericId) || numericId <= 0) {
            throw new Error("Invalid transaction ID: must be a positive integer");
        }

        const token = localStorage.getItem('accessToken');
        const headers = {
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.get(`${apiUrlOne}/payment-transactions/get-transaction/${numericId}`, { headers });
        return response.data;
    } catch (error) {
        console.error("Error fetching payment transaction:", error);
        throw error;
    }
};

/* Fetch all refund details for admin */
export const getRefundDetails = async (filters = {}) => {
    try {
        const params = new URLSearchParams();
        if (filters.page) params.append('page', filters.page);
        if (filters.limit) params.append('limit', filters.limit);
        if (filters.search) params.append('search', filters.search);
        if (filters.status) params.append('status', filters.status);

        const token = localStorage.getItem('accessToken');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const url = params.toString()
            ? `${apiUrlOne}/bookings/admin/refund-details?${params.toString()}`
            : `${apiUrlOne}/bookings/admin/refund-details`;

        const response = await axios.get(url, { headers });
        return response.data;
    } catch (error) {
        console.error("Error fetching refund details:", error);
        throw error;
    }
};

/* Update refund details for admin */
export const updateRefundDetails = async (id, data) => {
    try {
        const numericId = Number(id);
        if (!Number.isInteger(numericId) || numericId <= 0) {
            throw new Error("Invalid refund ID: must be a positive integer");
        }

        const token = localStorage.getItem('accessToken');
        const headers = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.put(`${apiUrlOne}/bookings/admin/refund-details/${numericId}`, data, { headers });
        return response.data;
    } catch (error) {
        console.error(`Error updating refund details for ID ${id}:`, error);
        throw error;
    }
};

/* Create a new booking */
export const createBooking = async (bookingData) => {
    try {
        const token = localStorage.getItem('accessToken');
        const headers = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.post(`${apiUrlOne}/bookings/admin/create`, bookingData, { headers });
        return response.data;
    } catch (error) {
        console.error("Error creating booking:", error);
        throw error;
    }
};

/* Save booking as draft */
export const saveBookingDraft = async (draftData) => {
    try {
        const token = localStorage.getItem('accessToken');
        const headers = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.post(`${apiUrlOne}/bookings/admin/draft`, draftData, { headers });
        return response.data;
    } catch (error) {
        console.error("Error saving booking draft:", error);
        throw error;
    }
};

/* Generate payment link for a booking */
export const generatePaymentLink = async (bookingId) => {
    try {
        const numericBookingId = Number(bookingId);
        if (!Number.isInteger(numericBookingId) || numericBookingId <= 0) {
            throw new Error("Invalid booking ID: must be a positive integer");
        }

        const token = localStorage.getItem('accessToken');
        const headers = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.post(`${apiUrlOne}/bookings/${numericBookingId}/payment-link`, {}, { headers });
        return response.data;
    } catch (error) {
        console.error("Error generating payment link:", error);
        throw error;
    }
};

/* Record offline payment */
export const recordOfflinePayment = async (paymentData) => {
    try {
        const token = localStorage.getItem('accessToken');
        const headers = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.post(`${apiUrlOne}/bookings/admin/offline-payment`, paymentData, { headers });
        return response.data;
    } catch (error) {
        console.error("Error recording offline payment:", error);
        throw error;
    }
};

/* Search for existing enquiries */
export const searchEnquiries = async (searchTerm) => {
    try {
        const token = localStorage.getItem('accessToken');
        const headers = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.get(`${apiUrlOne}/retreat-enquiry/admin/search?search=${encodeURIComponent(searchTerm)}`, { headers });
        return response.data;
    } catch (error) {
        console.error("Error searching enquiries:", error);
        throw error;
    }
};

/* Search for existing customers/users */
export const searchCustomers = async (searchTerm) => {
    try {
        const token = localStorage.getItem('accessToken');
        const headers = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.get(`${apiUrlOne}/users/admin/search?search=${encodeURIComponent(searchTerm)}`, { headers });
        return response.data;
    } catch (error) {
        console.error("Error searching customers:", error);
        throw error;
    }
};

/* Search for affiliates */
export const searchAffiliates = async (searchTerm) => {
    try {
        const token = localStorage.getItem('accessToken');
        const headers = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.get(`${apiUrlOne}/affiliates/admin/search?search=${encodeURIComponent(searchTerm)}`, { headers });
        return response.data;
    } catch (error) {
        console.error("Error searching affiliates:", error);
        throw error;
    }
};

/* Get retreats for dropdown */
export const getRetreatsList = async () => {
    try {
        const token = localStorage.getItem('accessToken');
        const headers = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.get(`${apiUrlOne}/retreats/admin/list`, { headers });
        return response.data;
    } catch (error) {
        console.error("Error fetching retreats list:", error);
        throw error;
    }
};

/* Get accommodations for a retreat */
export const getRetreatAccommodations = async (retreatId) => {
    try {
        const numericRetreatId = Number(retreatId);
        if (!Number.isInteger(numericRetreatId) || numericRetreatId <= 0) {
            throw new Error("Invalid retreat ID: must be a positive integer");
        }

        const token = localStorage.getItem('accessToken');
        const headers = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.get(`${apiUrlOne}/retreats/${numericRetreatId}/accommodations`, { headers });
        return response.data;
    } catch (error) {
        console.error("Error fetching retreat accommodations:", error);
        throw error;
    }
};

/* Get add-ons for a retreat */
export const getRetreatAddons = async (retreatId) => {
    try {
        const numericRetreatId = Number(retreatId);
        if (!Number.isInteger(numericRetreatId) || numericRetreatId <= 0) {
            throw new Error("Invalid retreat ID: must be a positive integer");
        }

        const token = localStorage.getItem('accessToken');
        const headers = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.get(`${apiUrlOne}/retreats/${numericRetreatId}/addons`, { headers });
        return response.data;
    } catch (error) {
        console.error("Error fetching retreat add-ons:", error);
        throw error;
    }
};

/* Check availability for dates */
export const checkAvailability = async (retreatId, accommodationId, startDate, endDate, guestCount) => {
    try {
        const numericRetreatId = Number(retreatId);
        if (!Number.isInteger(numericRetreatId) || numericRetreatId <= 0) {
            throw new Error("Invalid retreat ID: must be a positive integer");
        }

        const token = localStorage.getItem('accessToken');
        const headers = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const params = new URLSearchParams();
        if (accommodationId) params.append('accommodationId', Number(accommodationId));
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (guestCount) params.append('guestCount', Number(guestCount));

        const response = await axios.get(`${apiUrlOne}/retreats/${numericRetreatId}/availability?${params.toString()}`, { headers });
        return response.data;
    } catch (error) {
        console.error("Error checking availability:", error);
        throw error;
    }
};

/* Upload payment proof/receipt */
export const uploadPaymentProof = async (file) => {
    try {
        const token = localStorage.getItem('accessToken');
        const formData = new FormData();
        formData.append('file', file);

        const headers = {
            'Content-Type': 'multipart/form-data',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.post(`${apiUrlOne}/uploads/payment-proof`, formData, { headers });
        return response.data;
    } catch (error) {
        console.error("Error uploading payment proof:", error);
        throw error;
    }
};

/* Confirm booking by admin */
export const confirmBooking = async (bookingId) => {
    try {
        const numericBookingId = Number(bookingId);
        if (!Number.isInteger(numericBookingId) || numericBookingId <= 0) {
            throw new Error("Invalid booking ID: must be a positive integer");
        }

        const token = localStorage.getItem('accessToken');
        const headers = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.put(`${apiUrlOne}/bookings/admin/${numericBookingId}/confirm`, {}, { headers });
        return response.data;
    } catch (error) {
        console.error("Error confirming booking:", error);
        throw error;
    }
};
